import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ImageModule } from '@df/module-image/image.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ICmsComponent } from '@icms/core/content/component/icms-component.decorator';
import { TypeDropdownComponent } from 'projects/app-default/src/app/ui/common/type-dropdown/type-dropdown.component';
import { AbstractDeliveryInfoComponent } from '../../../delivery-info/abstract-delivery-info.component';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';

export namespace NDeliveryInformationComponent {
    export interface IComponentContent {
        title?: string;
        leftSectionTitle?: string;
        rightSectionTitle?: string;
        bottomItems?: IBottomItem[];
        informationItems?: IInformationItem[];
        returnsTitle?: string;
        returnsDescription?: string;
    }

    export interface IInformationItem {
        text?: string;
    }

    export interface IBottomItem {
        copy?: string;
        link?: string;
    }
}

@Component({
    selector: 'page-component-delivery-information',
    templateUrl: './delivery-information.html',
    styleUrls: ['./delivery-information.scss'],
    standalone: true,
    imports: [
        FormsModule,
        TranslationModule,
        ImageModule,
        ICmsClientModule,
        PriceComponent,
        TypeDropdownComponent,
        HrefComponent,
        SizeClassDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageComponentDeliveryInformationComponent>({
    id: 'delivery-information',
    name: 'Delivery Information',
    description: 'Component displaying delivery country selection and important delivery information',
    image: '/common/cms/delivery-information-component.png',
    tags: ['delivery', 'information', 'shipping'],
    fields: [
        // general
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-top',
            title: 'Top part'
        },
        {
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Main Title',
            default: 'Delivery',
            required: true
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-left',
            title: 'Form section'
        },
        {
            key: 'leftSectionTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Form Section Title',
            default: 'Select your Delivery country:',
            required: true
        },
        <NICmsContentField.IRepeaterField>{
            key: 'bottomItems',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Bottom Links',
            itemName: 'Row',
            fields: [
                {
                    key: 'copy',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Copy',
                    required: true
                },
                {
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link'
                }
            ]
        },
        {
            key: 'returnsTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Returns Title',
            default: 'Returns',
            required: true
        },
        {
            key: 'returnsDescription',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Returns Description',
            default: 'We accept returns and exchanges within 60 days of receipt.',
            required: true
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-right',
            title: 'Information section'
        },
        {
            key: 'rightSectionTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Section Title',
            default: 'Important Information:',
            required: true
        },
        // information items
        <NICmsContentField.IRepeaterField>{
            key: 'informationItems',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Delivery information notes',
            itemName: 'Section',
            fields: [
                {
                    key: 'text',
                    type: NICmsContentField.EFieldType.textarea,
                    title: 'Information Text',
                    required: true
                }
            ]
        }
    ]
})
export class PageComponentDeliveryInformationComponent extends AbstractDeliveryInfoComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: NDeliveryInformationComponent.IComponentContent;
}
